/* ─────────────────────────────────────────────
   TechIllustrations.tsx
   Reusable SVG illustrations for BETRobionics
   ───────────────────────────────────────────── */

/* ══ Circuit Board ══ */
export const CircuitBoardSVG = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 480 320" xmlns="http://www.w3.org/2000/svg"
    className={className} aria-hidden="true" fill="none">
    {/* Board outline */}
    <rect x="10" y="10" width="460" height="300" rx="8"
      fill="#f0fdf4" stroke="#1aa534" strokeWidth="1.5" />
    {/* Green traces */}
    <g stroke="#1aa534" strokeWidth="1.5" opacity="0.7">
      <polyline points="50,80 120,80 120,140 220,140 220,80 340,80 340,140 420,140" />
      <polyline points="50,200 100,200 100,160 200,160 200,240 360,240 360,200 420,200" />
      <polyline points="180,80 180,40 380,40 380,80" />
      <polyline points="260,140 260,240" />
      <polyline points="50,120 80,120 80,280 400,280 400,240" />
    </g>
    {/* Red traces */}
    <g stroke="#d2252e" strokeWidth="1" opacity="0.5">
      <polyline points="140,200 140,120 300,120 300,200" />
      <polyline points="50,160 60,160 60,260 160,260" />
      <polyline points="360,80 360,40" />
    </g>
    {/* IC chip centre */}
    <rect x="195" y="130" width="90" height="60" rx="4"
      fill="white" stroke="#1aa534" strokeWidth="1.5" />
    <text x="240" y="165" textAnchor="middle" fill="#1aa534"
      fontSize="10" fontFamily="monospace" fontWeight="bold">MCU</text>
    {/* Chip pins left */}
    {[140,155,170].map((y, i) => (
      <line key={`pl${i}`} x1="185" y1={y} x2="195" y2={y}
        stroke="#1aa534" strokeWidth="1.5" />
    ))}
    {/* Chip pins right */}
    {[140,155,170].map((y, i) => (
      <line key={`pr${i}`} x1="285" y1={y} x2="295" y2={y}
        stroke="#1aa534" strokeWidth="1.5" />
    ))}
    {/* Capacitors */}
    <rect x="105" y="72" width="10" height="16" rx="2"
      fill="white" stroke="#d2252e" strokeWidth="1.2" />
    <line x1="110" y1="70" x2="110" y2="65" stroke="#d2252e" strokeWidth="1.2" />
    <line x1="110" y1="90" x2="110" y2="95" stroke="#d2252e" strokeWidth="1.2" />
    <rect x="330" y="72" width="10" height="16" rx="2"
      fill="white" stroke="#d2252e" strokeWidth="1.2" />
    {/* Resistors */}
    <rect x="248" y="155" width="24" height="8" rx="2"
      fill="white" stroke="#1aa534" strokeWidth="1" />
    <line x1="236" y1="159" x2="248" y2="159" stroke="#1aa534" strokeWidth="1.2" />
    <line x1="272" y1="159" x2="284" y2="159" stroke="#1aa534" strokeWidth="1.2" />
    {/* Solder joints */}
    {[[120,80],[220,80],[340,80],[120,140],[220,140],[340,140],
      [260,140],[260,240],[100,200],[360,200]].map(([cx,cy],i) => (
      <circle key={i} cx={cx} cy={cy} r="4"
        fill="white" stroke={i%2===0?'#1aa534':'#d2252e'} strokeWidth="1.5" />
    ))}
    {/* Corner fiducials */}
    <circle cx="30" cy="30" r="5" fill="none" stroke="#1aa534" strokeWidth="1" />
    <circle cx="450" cy="30" r="5" fill="none" stroke="#1aa534" strokeWidth="1" />
    <circle cx="30" cy="295" r="5" fill="none" stroke="#1aa534" strokeWidth="1" />
    <circle cx="450" cy="295" r="5" fill="none" stroke="#1aa534" strokeWidth="1" />
  </svg>
);

/* ══ Neural Network ══ */
export const NeuralNetSVG = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 420 280" xmlns="http://www.w3.org/2000/svg"
    className={className} aria-hidden="true" fill="none">
    {/* Connection lines */}
    {([
      [60,60,160,50],[60,60,160,110],[60,60,160,170],
      [60,140,160,50],[60,140,160,110],[60,140,160,170],
      [60,220,160,110],[60,220,160,170],[60,220,160,230],
      [160,50,260,70],[160,50,260,140],[160,50,260,210],
      [160,110,260,70],[160,110,260,140],[160,110,260,210],
      [160,170,260,70],[160,170,260,140],[160,170,260,210],
      [160,230,260,140],[160,230,260,210],
      [260,70,360,110],[260,70,360,170],
      [260,140,360,110],[260,140,360,170],
      [260,210,360,110],[260,210,360,170],
    ] as [number,number,number,number][]).map(([x1,y1,x2,y2],i) => (
      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={i%3===0?'#1aa534':'#94a3b8'} strokeWidth="0.7" opacity="0.4" />
    ))}
    {/* Input layer */}
    {[60,140,220].map((y,i) => (
      <g key={`in${i}`}>
        <circle cx={60} cy={y} r={8} fill="white" stroke="#1aa534" strokeWidth="2" />
        <circle cx={60} cy={y} r={3} fill="#1aa534" />
      </g>
    ))}
    {/* Hidden layer */}
    {[50,110,170,230].map((y,i) => (
      <g key={`h${i}`}>
        <circle cx={160} cy={y} r={8} fill="white"
          stroke={i%2===0?'#1aa534':'#d2252e'} strokeWidth="2" />
        <circle cx={160} cy={y} r={3} fill={i%2===0?'#1aa534':'#d2252e'} />
      </g>
    ))}
    {/* Hidden layer 2 */}
    {[70,140,210].map((y,i) => (
      <g key={`h2${i}`}>
        <circle cx={260} cy={y} r={8} fill="white" stroke="#d2252e" strokeWidth="2" />
        <circle cx={260} cy={y} r={3} fill="#d2252e" />
      </g>
    ))}
    {/* Output layer */}
    {[110,170].map((y,i) => (
      <g key={`out${i}`}>
        <circle cx={360} cy={y} r={10} fill="white" stroke="#1aa534" strokeWidth="2.5" />
        <circle cx={360} cy={y} r={4} fill="#1aa534" />
      </g>
    ))}
    {/* Labels */}
    <text x="30" y="148" fill="#64748b" fontSize="8" fontFamily="monospace">IN</text>
    <text x="148" y="270" fill="#64748b" fontSize="8" fontFamily="monospace">HIDDEN</text>
    <text x="344" y="195" fill="#64748b" fontSize="8" fontFamily="monospace">OUT</text>
    {/* Animated data pulses */}
    <circle cx="110" cy="55" r="3" fill="#1aa534" opacity="0.7">
      <animateMotion dur="2s" repeatCount="indefinite"
        path="M-50,-5 L50,55" />
    </circle>
    <circle cx="210" cy="140" r="3" fill="#d2252e" opacity="0.7">
      <animateMotion dur="2.5s" repeatCount="indefinite"
        path="M-50,0 L50,0" />
    </circle>
  </svg>
);

/* ══ Smart Bin (Poubelle intelligente) ══ */
export const SmartBinSVG = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 200 260" xmlns="http://www.w3.org/2000/svg"
    className={className} aria-hidden="true" fill="none">
    {/* Bin body */}
    <rect x="40" y="70" width="120" height="150" rx="10"
      fill="#f0fdf4" stroke="#1aa534" strokeWidth="2" />
    {/* Lid */}
    <rect x="30" y="55" width="140" height="22" rx="6"
      fill="white" stroke="#1aa534" strokeWidth="2" />
    {/* Handle */}
    <rect x="80" y="40" width="40" height="18" rx="9"
      fill="white" stroke="#1aa534" strokeWidth="2" />
    {/* Screen on bin */}
    <rect x="55" y="90" width="90" height="55" rx="5"
      fill="#1aa534" opacity="0.08" stroke="#1aa534" strokeWidth="1" />
    {/* WiFi symbol on screen */}
    <path d="M100,130 m-16,-12 a20,20 0 0,1 32,0" stroke="#1aa534" strokeWidth="2" strokeLinecap="round" fill="none" />
    <path d="M100,130 m-10,-6 a13,13 0 0,1 20,0" stroke="#1aa534" strokeWidth="2" strokeLinecap="round" fill="none" />
    <circle cx="100" cy="130" r="3" fill="#1aa534" />
    {/* Level indicator bar */}
    <rect x="55" y="155" width="90" height="8" rx="4"
      fill="#e2e8f0" stroke="#94a3b8" strokeWidth="0.5" />
    <rect x="55" y="155" width="60" height="8" rx="4" fill="#1aa534" />
    <text x="100" y="177" textAnchor="middle" fill="#64748b"
      fontSize="8" fontFamily="monospace">FILL: 67%</text>
    {/* IoT signal lines */}
    <line x1="100" y1="55" x2="100" y2="25" stroke="#1aa534" strokeWidth="1" strokeDasharray="3 3" />
    <circle cx="100" cy="22" r="4" fill="#1aa534" opacity="0.5">
      <animate attributeName="r" values="4;8;4" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" />
    </circle>
    {/* Wheels */}
    <circle cx="65" cy="218" r="8" fill="white" stroke="#94a3b8" strokeWidth="1.5" />
    <circle cx="135" cy="218" r="8" fill="white" stroke="#94a3b8" strokeWidth="1.5" />
    {/* Status LED */}
    <circle cx="148" cy="80" r="5" fill="#1aa534">
      <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
    </circle>
    {/* Cloud connection arrow */}
    <text x="120" y="22" fill="#1aa534" fontSize="7" fontFamily="monospace">CLOUD</text>
    <path d="M118,14 L118,8 L165,8" stroke="#1aa534" strokeWidth="1" fill="none" strokeDasharray="3 2" />
  </svg>
);

/* ══ IoT Node Network ══ */
export const IoTNodesSVG = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 440 280" xmlns="http://www.w3.org/2000/svg"
    className={className} aria-hidden="true" fill="none">
    {/* Connection lines */}
    <g stroke="#1aa534" strokeWidth="1" strokeDasharray="5 3" opacity="0.4">
      <line x1="80" y1="80" x2="220" y2="140" />
      <line x1="220" y1="140" x2="360" y2="80" />
      <line x1="220" y1="140" x2="140" y2="220" />
      <line x1="220" y1="140" x2="300" y2="220" />
      <line x1="80" y1="80" x2="140" y2="220" />
      <line x1="360" y1="80" x2="300" y2="220" />
      <line x1="40" y1="180" x2="80" y2="80" />
      <line x1="40" y1="180" x2="140" y2="220" />
      <line x1="400" y1="180" x2="360" y2="80" />
      <line x1="400" y1="180" x2="300" y2="220" />
    </g>
    {/* Central gateway */}
    <circle cx="220" cy="140" r="22" fill="#f0fdf4" stroke="#1aa534" strokeWidth="2.5" />
    <text x="220" y="137" textAnchor="middle" fill="#1aa534"
      fontSize="8" fontFamily="monospace" fontWeight="bold">GW</text>
    <text x="220" y="148" textAnchor="middle" fill="#1aa534"
      fontSize="6" fontFamily="monospace">LoRa</text>
    {/* Sensor nodes */}
    {[
      [80,80,'TEMP','#1aa534'],[360,80,'HUM','#1aa534'],
      [140,220,'SOIL','#d2252e'],[300,220,'FLOW','#d2252e'],
      [40,180,'CO2','#1aa534'],[400,180,'LUX','#d2252e'],
    ].map(([cx,cy,label,color],i) => (
      <g key={i}>
        <circle cx={cx as number} cy={cy as number} r="16"
          fill="white" stroke={color as string} strokeWidth="2" />
        <text x={cx as number} y={(cy as number)-2} textAnchor="middle"
          fill={color as string} fontSize="7" fontFamily="monospace" fontWeight="bold">
          {label}
        </text>
        <text x={cx as number} y={(cy as number)+8} textAnchor="middle"
          fill="#94a3b8" fontSize="6" fontFamily="monospace">node</text>
        {/* Pulse ring */}
        <circle cx={cx as number} cy={cy as number} r="20"
          fill="none" stroke={color as string} strokeWidth="1" opacity="0.3">
          <animate attributeName="r" values="16;24;16" dur={`${1.5+i*0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0;0.3" dur={`${1.5+i*0.3}s`} repeatCount="indefinite" />
        </circle>
      </g>
    ))}
    {/* Data packets moving */}
    <circle r="3" fill="#1aa534" opacity="0.8">
      <animateMotion dur="3s" repeatCount="indefinite"
        path="M80,80 L220,140" />
    </circle>
    <circle r="3" fill="#d2252e" opacity="0.8">
      <animateMotion dur="2.5s" repeatCount="indefinite"
        path="M220,140 L300,220" />
    </circle>
    {/* Cloud icon top */}
    <path d="M195,18 a12,12 0 0,1 24,0 a8,8 0 0,1 0,16 H195 a8,8 0 0,1 0,-16"
      fill="white" stroke="#1aa534" strokeWidth="1.5" />
    <line x1="220" y1="34" x2="220" y2="115" stroke="#1aa534"
      strokeWidth="1" strokeDasharray="4 3" />
  </svg>
);

/* ══ Sensor / Chip ══ */
export const SensorChipSVG = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
    className={className} aria-hidden="true" fill="none">
    {/* Chip body */}
    <rect x="55" y="55" width="90" height="90" rx="6"
      fill="#f0fdf4" stroke="#1aa534" strokeWidth="2" />
    {/* Inner die */}
    <rect x="70" y="70" width="60" height="60" rx="3"
      fill="white" stroke="#1aa534" strokeWidth="1" />
    {/* Die pattern */}
    <line x1="70" y1="100" x2="130" y2="100" stroke="#1aa534" strokeWidth="0.6" opacity="0.4" />
    <line x1="100" y1="70" x2="100" y2="130" stroke="#1aa534" strokeWidth="0.6" opacity="0.4" />
    <circle cx="100" cy="100" r="8" fill="#1aa534" opacity="0.15" />
    <circle cx="100" cy="100" r="4" fill="#1aa534" opacity="0.6" />
    {/* Pins top */}
    {[70,85,100,115,130].map((x,i) => (
      <line key={`pt${i}`} x1={x} y1="55" x2={x} y2="42"
        stroke="#d2252e" strokeWidth="2" strokeLinecap="round" />
    ))}
    {/* Pins bottom */}
    {[70,85,100,115,130].map((x,i) => (
      <line key={`pb${i}`} x1={x} y1="145" x2={x} y2="158"
        stroke="#d2252e" strokeWidth="2" strokeLinecap="round" />
    ))}
    {/* Pins left */}
    {[70,85,100,115,130].map((y,i) => (
      <line key={`pl${i}`} x1="55" y1={y} x2="42" y2={y}
        stroke="#1aa534" strokeWidth="2" strokeLinecap="round" />
    ))}
    {/* Pins right */}
    {[70,85,100,115,130].map((y,i) => (
      <line key={`pr${i}`} x1="145" y1={y} x2="158" y2={y}
        stroke="#1aa534" strokeWidth="2" strokeLinecap="round" />
    ))}
    {/* Label */}
    <text x="100" y="103" textAnchor="middle" fill="#1aa534"
      fontSize="9" fontFamily="monospace" fontWeight="bold">MCU</text>
  </svg>
);

/* ══ Robotic Arm ══ */
export const RoboticArmSVG = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 240 260" xmlns="http://www.w3.org/2000/svg"
    className={className} aria-hidden="true" fill="none">
    {/* Base */}
    <rect x="80" y="220" width="80" height="20" rx="4"
      fill="#f0fdf4" stroke="#1aa534" strokeWidth="2" />
    <rect x="100" y="210" width="40" height="14" rx="3"
      fill="white" stroke="#1aa534" strokeWidth="1.5" />
    {/* Lower arm */}
    <rect x="108" y="145" width="24" height="68" rx="8"
      fill="white" stroke="#1aa534" strokeWidth="2" />
    {/* Joint 1 */}
    <circle cx="120" cy="148" r="12" fill="white" stroke="#1aa534" strokeWidth="2.5" />
    <circle cx="120" cy="148" r="5" fill="#1aa534" opacity="0.3" />
    {/* Upper arm (angled) */}
    <rect x="98" y="90" width="20" height="65" rx="7"
      transform="rotate(-25 108 148)"
      fill="white" stroke="#d2252e" strokeWidth="2" />
    {/* Joint 2 */}
    <circle cx="97" cy="95" r="11" fill="white" stroke="#d2252e" strokeWidth="2.5" />
    <circle cx="97" cy="95" r="4" fill="#d2252e" opacity="0.3" />
    {/* Forearm */}
    <rect x="68" y="50" width="18" height="55" rx="6"
      transform="rotate(20 80 95)"
      fill="white" stroke="#1aa534" strokeWidth="2" />
    {/* Joint 3 */}
    <circle cx="72" cy="52" r="9" fill="white" stroke="#1aa534" strokeWidth="2" />
    <circle cx="72" cy="52" r="3" fill="#1aa534" opacity="0.4" />
    {/* Gripper */}
    <line x1="58" y1="22" x2="45" y2="8" stroke="#1aa534" strokeWidth="3" strokeLinecap="round" />
    <line x1="58" y1="22" x2="71" y2="8" stroke="#1aa534" strokeWidth="3" strokeLinecap="round" />
    <circle cx="58" cy="22" r="5" fill="white" stroke="#1aa534" strokeWidth="2" />
    {/* Held object */}
    <rect x="48" y="2" width="20" height="8" rx="3"
      fill="#d2252e" opacity="0.4" stroke="#d2252e" strokeWidth="1" />
  </svg>
);
